/**
 * @module HypothesisCorrelationsSection
 * @purpose Renders network, lineage, and temporal correlated-evidence items from the context envelope.
 * @depends types/reasoning (CorrelatedEvidence, CorrelationAxis), react-i18next
 * @dependedOnBy HypothesisCard (step 5)
 *
 * @rules
 * - Performance: pure display — props only, no state, no data fetching
 * - State: none
 * - Errors: n/a — purely presentational; renders empty message when no matching axes
 */

import { useTranslation } from 'react-i18next'
import type { CorrelatedEvidence } from '../../../../../types/reasoning'

interface HypothesisCorrelationsSectionProps {
  correlatedEvidence: CorrelatedEvidence[]
}

interface AxisGroupProps {
  axisLabel: string
  items: CorrelatedEvidence[]
}

interface CorrelationItemProps {
  item: CorrelatedEvidence
  index: number
}

const RENDERED_AXES = ['network', 'lineage', 'temporal'] as const
type RenderedAxis = (typeof RENDERED_AXES)[number]

const CONTAINER_STYLE: React.CSSProperties = {
  padding: '4px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '8px',
}

const AXIS_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--accent)',
  marginBottom: '6px',
  marginTop: '12px',
  borderBottom: '1px solid var(--border)',
  paddingBottom: '3px',
}

const ITEM_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '8px 10px',
  marginBottom: '6px',
}

const REASON_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text)',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const BASENAME_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text)',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

function CorrelationItem({ item, index }: CorrelationItemProps): React.ReactElement {
  return (
    <div style={ITEM_STYLE} key={item.observation_id ?? `item-${String(index)}`}>
      {item.basename !== null && (
        <div style={{ marginBottom: '4px' }}>
          <span style={BASENAME_CHIP}>{item.basename}</span>
        </div>
      )}
      <div style={REASON_STYLE}>{item.correlation_reason}</div>
    </div>
  )
}

function AxisGroup({ axisLabel, items }: AxisGroupProps): React.ReactElement {
  return (
    <div role="group" aria-label={axisLabel}>
      <div style={AXIS_HEADING} aria-hidden="true">{axisLabel}</div>
      {items.map((item, index) => (
        <CorrelationItem
          key={item.observation_id ?? `${axisLabel}-${String(index)}`}
          item={item}
          index={index}
        />
      ))}
    </div>
  )
}

/**
 * Renders network, lineage, and temporal correlated-evidence from the context envelope.
 *
 * @behaviour Groups items by correlation_axis; renders each item's correlation_reason as
 *   a neutral fact string with basename (mono chip) when present. Spatial and identity axes
 *   are excluded — spatial is shown in Co-located Files; identity is handled by the deferred
 *   actor section. Empty state (no matching axes) renders a neutral message.
 * @errors n/a
 */
export function HypothesisCorrelationsSection({ correlatedEvidence }: HypothesisCorrelationsSectionProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const axisLabelMap: Record<RenderedAxis, string> = {
    network: t('reasoning.hypothesis.correlations.axis.network'),
    lineage: t('reasoning.hypothesis.correlations.axis.lineage'),
    temporal: t('reasoning.hypothesis.correlations.axis.temporal'),
  }

  const grouped: Partial<Record<RenderedAxis, CorrelatedEvidence[]>> = {}
  for (const item of correlatedEvidence) {
    if (!(RENDERED_AXES as ReadonlyArray<string>).includes(item.correlation_axis)) {
      continue
    }
    const axis = item.correlation_axis as RenderedAxis
    grouped[axis] = [...(grouped[axis] ?? []), item]
  }

  const hasAny = RENDERED_AXES.some((axis) => (grouped[axis]?.length ?? 0) > 0)

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.correlations.heading')}</div>
      {!hasAny
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.correlations.empty')}</div>
        : RENDERED_AXES.filter((axis) => (grouped[axis]?.length ?? 0) > 0).map((axis) => (
          <AxisGroup
            key={axis}
            axisLabel={axisLabelMap[axis]}
            items={grouped[axis] ?? []}
          />
        ))
      }
    </div>
  )
}
